import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNexauraData } from "../context/NexauraContext";
import { useToast } from "../components/common/ToastContext";
import Card from "../components/common/Card";
import { fmtNumber } from "../utils/format";

const LEVELS = 20;

export default function Team() {
  const { data, fetchReferrals, fetchUserPackages } = useNexauraData();
  const toast = useToast();

  const [board, setBoard] = useState(1);
  const [open, setOpen] = useState(null);
  const [loadingLvl, setLoadingLvl] = useState(0);
  const [, force] = useState(0); // re-render after cache fills

  // Shared caches across package switches (tree is board-independent).
  const tree = useRef({}); // levelNo -> [ids]
  const pkgs = useRef({}); // id -> bool[12]

  // Default the selector to the user's current package.
  useEffect(() => {
    setBoard(data.highestBoard || 1);
    tree.current = { 1: data.referrals || [] };
    pkgs.current = {};
    setOpen(null);
  }, [data.userId, data.highestBoard, data.referrals]);

  const ensureTree = useCallback(
    async (target) => {
      if (!tree.current[1]) tree.current[1] = data.referrals || [];
      for (let l = 2; l <= target; l++) {
        if (tree.current[l]) continue;
        const prev = tree.current[l - 1] || [];
        const members = [];
        for (const id of prev) members.push(...(await fetchReferrals(id)));
        tree.current[l] = members;
      }
    },
    [data.referrals, fetchReferrals]
  );

  const ensurePkgs = useCallback(
    async (ids) => {
      for (const id of ids) {
        if (!pkgs.current[id]) pkgs.current[id] = await fetchUserPackages(id);
      }
    },
    [fetchUserPackages]
  );

  const toggle = async (l) => {
    if (open === l) return setOpen(null);
    setLoadingLvl(l);
    await ensureTree(l);
    await ensurePkgs(tree.current[l] || []);
    setLoadingLvl(0);
    setOpen(l);
    force((x) => x + 1);
  };

  // Re-filter when board changes (cached pkgs make this instant).
  useEffect(() => { force((x) => x + 1); }, [board]);

  // Members at a level who are ACTIVE on the selected board.
  const membersForBoard = (l) => {
    const ids = tree.current[l];
    if (!ids) return null; // not loaded yet
    return ids.filter((id) => pkgs.current[id] && pkgs.current[id][board - 1]);
  };

  const refLink = `${window.location.origin}${window.location.pathname}?ref=${data.userId}`;
  const copy = () => {
    try {
      navigator.clipboard.writeText(refLink);
      toast.success("Copied", "Referral link copied.");
    } catch {
      toast.error("Copy failed", "Long-press to copy manually.");
    }
  };

  const directCount = data.details ? data.details.totalDirectReferrals : 0;

  return (
    <div className="screen">
      <div className="screen-title">Team</div>

      <div className="stat-grid">
        <div className="stat">
          <div className="s-label">Direct Team</div>
          <div className="s-value">{fmtNumber(directCount)}</div>
        </div>
        <div className="stat">
          <div className="s-label">Levels Unlocked</div>
          <div className="s-value">{data.levelsUnlocked}/20</div>
        </div>
      </div>

      <Card title="Your Referral Link" className="section-gap">
        <div className="ref-box">
          <span className="ref-text">{refLink}</span>
          <button className="ref-copy" onClick={copy}>Copy</button>
        </div>
      </Card>

      {/* Package selector */}
      <div className="card-title" style={{ marginLeft: 2 }}>Select Package</div>
      <div className="board-picker">
        {Array.from({ length: 12 }, (_, i) => {
          const bId = i + 1;
          const owned = data.packages[i];
          return (
            <button
              key={bId}
              className={`bp-pill ${board === bId ? "active" : ""} ${owned ? "owned" : ""}`}
              onClick={() => { setBoard(bId); setOpen(null); }}
            >
              B{bId}
            </button>
          );
        })}
      </div>
      <div className="screen-sub" style={{ margin: "0 2px 14px" }}>
        20-level team for <b style={{ color: "var(--text-hi)" }}>Board {board}</b> — tap a level to expand.
      </div>

      {/* 20 levels */}
      {Array.from({ length: LEVELS }, (_, i) => {
        const l = i + 1;
        const isOpen = open === l;
        const isLoading = loadingLvl === l;
        const unlocked = l <= data.levelsUnlocked;
        const members = isOpen ? membersForBoard(l) : tree.current[l] ? membersForBoard(l) : null;
        return (
          <div key={l} className={`accordion lvl ${unlocked ? "unlocked" : "locked"} ${isOpen ? "open" : ""}`}>
            <button className="acc-head" onClick={() => toggle(l)}>
              <span className="acc-lvl">
                Level {l}
                <span className={`lvl-tag ${unlocked ? "on" : "off"}`}>
                  {unlocked ? "✓ Unlocked" : "🔒 Locked"}
                </span>
              </span>
              <span className="acc-meta">
                {isLoading ? (
                  <span className="spinner dark" />
                ) : members != null ? (
                  `${members.length} on B${board}`
                ) : (
                  "tap to load"
                )}
                <span className={`chev ${isOpen ? "up" : ""}`}>›</span>
              </span>
            </button>
            {isOpen && (
              <div className="acc-body">
                {members && members.length > 0 ? (
                  <div className="member-grid">
                    {members.map((id) => (
                      <span className="member-chip" key={id}>#{id}</span>
                    ))}
                  </div>
                ) : (
                  <div className="empty" style={{ padding: "18px 8px" }}>
                    No members active on Board {board} at this level.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
